import React from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Typography,
  Descriptions,
  message,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { GetReq } from '@/api/types/getReq';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Title } = Typography;

interface ReviewUserModalProps {
  open: boolean;
  onClose: () => void;
  userData?: GetReq;
  refetch: () => void;
}

export const ReviewUserModal: React.FC<ReviewUserModalProps> = ({
  open,
  onClose,
  userData,
  refetch,
}) => {
  const { translate } = useTranslation();
  const [form] = Form.useForm();
  const { usePost } = useAPI();
  const { mutateAsync: approveRequest, isLoading: approveLoading } = usePost(
    endPoints.approveRequest(String(userData?.id))
  );
  const { mutateAsync: rejectRequest, isLoading: rejectLoading } = usePost(
    endPoints.rejectRequest(String(userData?.id))
  );
  const handleSubmit = async (action: 'approve' | 'reject') => {
    if (action === 'approve') {
      approveRequest(
        {},
        {
          onSuccess: () => {
            message.success(translate('requests-user.success.approve'));
            refetch();
            onClose();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    } else {
      const feedback = form.getFieldValue('feedback');
      if (!feedback) {
        message.error(translate('requests-user.error.feedback-required'));
        return;
      }
      rejectRequest(
        { feedback },
        {
          onSuccess: () => {
            message.success(translate('requests-user.success.reject'));
            refetch();
            onClose();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    }
  };

  return (
    <Modal
      title={
        <Title level={4} className="mb-0">
          {translate('users.review')}
        </Title>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} icon={<CloseOutlined />}>
          {translate('buttons.cancel')}
        </Button>,
        <Button
          key="reject"
          danger
          icon={<CloseOutlined />}
          onClick={() => handleSubmit('reject')}
          loading={rejectLoading}
        >
          {translate('buttons.reject')}
        </Button>,
        <Button
          key="approve"
          className="bg-green-500 border-green-500"
          icon={<CheckOutlined />}
          onClick={() => handleSubmit('approve')}
          loading={approveLoading}
        >
          {translate('buttons.approve')}
        </Button>,
      ]}
    >
      <div className="mb-6">
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label={translate('users.name')}>
            {userData?.user_name}
          </Descriptions.Item>
          <Descriptions.Item label={translate('users.email')}>
            {userData?.user_email}
          </Descriptions.Item>
          <Descriptions.Item label={translate('users.universityId')}>
            {userData?.user_university_id}
          </Descriptions.Item>
          <Descriptions.Item label={translate('users.gender')}>
            {translate(`users.gender.${userData?.user_gender}`)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('users.dateOfBirth')}>
            {dayjs(userData?.user_date_of_birth).format('DD/MM/YYYY')}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          name="feedback"
          label={translate('users.feedback')}
          rules={[
            {
              required: userData?.status === 'REJECTED',
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder={translate('users.feedbackPlaceholder')}
            className="mt-2"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
